import { Product, CostCenter } from '@portal/portal-core';
import { TOOL_BUTTON_NAMES } from 'constants/super-admin';
import { LICENSE_STATUS } from 'constants/super-admin';
import { getCountryOptions } from 'utils';

// Minimal shape of the domain objects passed to filter `func` predicates.
// Filters are applied to both license objects and user objects, so the
// relevant fields are optional to cover the union of shapes accessed here.
interface FilterItem {
  productAccess: {
    name?: string;
    activityState?: string;
    status?: string;
    type?: string;
  };
  costCenter: {
    type?: string;
    countryCode?: string;
  };
  verified?: boolean;
  phantom?: boolean;
  accountState?: string;
  countryCode?: string;
}

interface FilterOption {
  label: string;
  id: string;
  func: (item: FilterItem) => boolean;
  productName?: string;
  activityState?: string;
  productAccessType?: string;
  tool?: string;
  countryCode?: string;
}

interface CountryOption {
  label: string;
  value: string;
}

// LICENSE FILTERS

const generateProductFilter = (specifiedProduct: string): FilterOption[] => {
  return [
    {
      label: Product.PRODUCT_DISPLAY_NAMES[specifiedProduct],
      id: `LICENSE_${specifiedProduct}`,
      func: (item: FilterItem) => item.productAccess.name === specifiedProduct,
      productName: specifiedProduct,
    },
  ];
};

export const licensedProductRadios = (specifiedProduct: string | undefined = undefined): FilterOption[] => {
  if (specifiedProduct) {
    return generateProductFilter(specifiedProduct);
  }
  const radios: FilterOption[] = [];
  const licensedProductList = Object.values(Product.NAMES_LICENSED_PRODUCTS) as string[];
  licensedProductList.forEach((productName: string) => {
    radios.push({
      label: Product.PRODUCT_DISPLAY_NAMES[productName],
      id: `LICENSE_${productName}`,
      func: (item: FilterItem) => item.productAccess.name === productName,
      productName,
    });
  });
  return radios;
};

export const FACS_WORKFLOW_MANAGER: FilterOption = {
  label: 'FACS Workflow Manager',
  id: 'LICENSE_FWM',
  func: (item: FilterItem) => item.productAccess.name === Product.NAMES.facsWorkflowManager,
  productName: Product.NAMES.facsWorkflowManager,
};

export const PENDING: FilterOption = {
  label: 'Pending',
  id: 'LICENSE_PENDING',
  func: (item: FilterItem) => item.productAccess.activityState === CostCenter.ACTIVITY_STATES.pending,
  activityState: CostCenter.ACTIVITY_STATES.pending,
};

export const ACTIVE: FilterOption = {
  label: 'Active',
  id: 'LICENSE_ACTIVE',
  func: (item: FilterItem) => item.productAccess.activityState === CostCenter.ACTIVITY_STATES.active,
  activityState: CostCenter.ACTIVITY_STATES.active,
};

export const CANCELLED: FilterOption = {
  label: 'Cancelled',
  id: 'LICENSE_CANCELLED',
  func: (item: FilterItem) => item.productAccess.activityState === CostCenter.ACTIVITY_STATES.cancelled,
  activityState: CostCenter.ACTIVITY_STATES.cancelled,
};

export const SUSPENDED: FilterOption = {
  label: 'Suspended',
  id: 'LICENSE_SUSPENDED',
  func: (item: FilterItem) => item.productAccess.activityState === CostCenter.ACTIVITY_STATES.suspended,
  activityState: CostCenter.ACTIVITY_STATES.suspended,
};

export const STATUS_ACTIVE: FilterOption = {
  label: LICENSE_STATUS.ACTIVE,
  id: 'LICENSE_STATUS_ACTIVE',
  func: (item: FilterItem) => item.productAccess.status === LICENSE_STATUS.ACTIVE,
};

export const STATUS_INACTIVE: FilterOption = {
  label: LICENSE_STATUS.INACTIVE,
  id: 'LICENSE_STATUS_INACTIVE',
  func: (item: FilterItem) => item.productAccess.status === LICENSE_STATUS.INACTIVE,
};

export const BETA: FilterOption = {
  label: 'Beta',
  id: 'LICENSE_BETA',
  func: (item: FilterItem) => item.productAccess.type === Product.TYPES.beta,
  productAccessType: Product.TYPES.beta,
};

export const TRIAL: FilterOption = {
  label: 'Trial',
  id: 'LICENSE_TRIAL',
  func: (item: FilterItem) => item.productAccess.type === Product.TYPES.trial,
  productAccessType: Product.TYPES.trial,
};

export const RELEASE: FilterOption = {
  label: 'Release',
  id: 'LICENSE_RELEASE',
  func: (item: FilterItem) => item.productAccess.type === Product.TYPES.release,
  productAccessType: Product.TYPES.release,
};

export const TRAINING: FilterOption = {
  label: 'Training',
  id: 'LICENSE_TRAINING',
  func: (item: FilterItem) => item.productAccess.type === Product.TYPES.training,
  productAccessType: Product.TYPES.training,
};

// LICENSE TOOL FILTERS
export const INSTITUTION_LICENSE: FilterOption = {
  label: 'Institution License',
  id: 'INSTITUTION_LICENSE',
  func: (item: FilterItem) => item.costCenter.type === 'institutional',
  tool: TOOL_BUTTON_NAMES.INSTITUTION_LICENSE,
};

export const INDIVIDUAL_LICENSE: FilterOption = {
  label: 'Individual License',
  id: 'INDIVIDUAL_LICENSE',
  func: (item: FilterItem) => item.costCenter.type === 'individual',
  tool: TOOL_BUTTON_NAMES.INDIVIDUAL_LICENSE,
};

// USER FILTERS
export const EMAIL_VERIFIED: FilterOption = {
  label: 'Verified',
  id: 'EMAIL_VERIFIED',
  func: (item: FilterItem) => item.verified === true,
};

export const EMAIL_UNVERIFIED: FilterOption = {
  label: 'Unverified',
  id: 'EMAIL_UNVERIFIED',
  func: (item: FilterItem) => item.verified !== true,
};

export const PHANTOM_USER: FilterOption = {
  label: 'Phantom',
  id: 'PHANTOM_USER',
  func: (item: FilterItem) => item.phantom === true,
};

export const REGISTERED_USER: FilterOption = {
  label: 'Regular',
  id: 'REGISTERED_USER',
  func: (item: FilterItem) => item.phantom !== true,
};

export const USER_ACTIVE: FilterOption = {
  label: 'Active',
  id: 'USER_ACTIVE',
  func: (item: FilterItem) => item.accountState === 'active',
};

export const USER_INACTIVE: FilterOption = {
  label: 'Inactive',
  id: 'USER_INACTIVE',
  func: (item: FilterItem) => item.accountState !== 'active',
};

// COUNTRY FILTERS
export const countryFilters = (): FilterOption[] => {
  return getCountryOptions().map((country: CountryOption) => ({
    label: country.label,
    id: `COUNTRY_${country.value}`,
    func: (item: FilterItem) => {
      // Support both user objects (item.countryCode) and license objects (item.costCenter.countryCode)
      const countryCode = item.countryCode || item.costCenter?.countryCode;
      return countryCode === country.value;
    },
    countryCode: country.value,
  }));
};
